<template>
  <div class="accounting-ledger-integration">
    <div class="p-0 m-0 columns">
      <div class="p-0 m-0 column">
        <div class="my-4 is-size-3">Accounting Ledger Integration</div>
      </div>
    </div>

    <div v-if="isLoading" class="container">
      <div>Loading accounting ledger information</div>
      <b-progress></b-progress>
    </div>

    <div v-else-if="isSystemManaged" class="line-items">
      <div class="p-3 m-0 columns is-gapless is-vcentered">
        <div class="line-item-details column">
          <div v-if="currentMapping.accountingLedgerHumanId" class="system-managed-text">
            Sage Customer ID:
            <strong>{{ currentMapping.accountingLedgerHumanId }}</strong>
          </div>
          <div v-else class="system-managed-text has-text-warning">
            <strong>System key (Sage ID not yet linked):</strong>
            {{ currentMapping.accountingLedgerId }}
          </div>
        </div>
      </div>
    </div>

    <div v-else class="line-items">
      <div class="p-3 m-0 columns is-gapless is-vcentered">
        <div class="line-item-details column">
          <div class="line-item-info">
            <div class="columns">
              <div class="column is-half">
                <b-field label="Accounting Ledger Id">
                  <b-input
                    v-model="accountingLedgerHumanId"
                    placeholder="Enter Accounting Ledger Id"
                    :disabled="isSaving"
                  ></b-input>
                </b-field>
              </div>
            </div>
          </div>

          <div class="line-item-info buttons-container">
            <b-button
              class="is-small is-primary"
              icon-left="save"
              @click="saveAccountingLedger"
              :loading="isSaving"
              :disabled="!hasChanges || isSaving"
            >
              {{ currentMapping ? 'Update' : 'Save' }}
            </b-button>
            <b-button
              v-if="currentMapping"
              class="is-small is-danger"
              icon-left="trash"
              @click="clearAccountingLedger"
              :disabled="isSaving"
            >
              Clear
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AccountingLedgerService from '@/service/accountingLedger.service';

export default {
  name: 'AccountingLedgerIntegration',
  props: {
    customerId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      accountingLedgerHumanId: '',
      currentMapping: null,
      isLoading: true,
      isSaving: false,
      originalLedgerHumanId: '',
      isSystemManaged: false,
    };
  },
  computed: {
    hasChanges() {
      return this.accountingLedgerHumanId !== this.originalLedgerHumanId;
    },
  },
  async mounted() {
    await this.loadAccountingLedger();
  },
  methods: {
    async loadAccountingLedger() {
      try {
        this.isLoading = true;
        this.currentMapping = await AccountingLedgerService.getCustomerLedgerMapping(this.customerId);

        // Scenario 1: No record found - allow creation
        if (!this.currentMapping) {
          this.accountingLedgerHumanId = '';
          this.originalLedgerHumanId = '';
          this.isSystemManaged = false;
          return;
        }

        // Scenario 3a: Record was auto-bound by the system (has accountingLedgerId).
        // Show read-only display regardless of whether humanId is available.
        if (this.currentMapping.accountingLedgerId) {
          this.isSystemManaged = true;
          this.accountingLedgerHumanId = '';
          this.originalLedgerHumanId = '';
          return;
        }

        // Scenario 2: Manually-bound record (no accountingLedgerId, has humanId) - allow edit/update/delete
        if (this.currentMapping.accountingLedgerHumanId) {
          this.accountingLedgerHumanId = this.currentMapping.accountingLedgerHumanId;
          this.originalLedgerHumanId = this.accountingLedgerHumanId;
          this.isSystemManaged = false;
          return;
        }
      } catch (error) {
        console.error('Error loading accounting ledger:', error);
        this.notify('Error loading accounting ledger information', 'is-danger');
      } finally {
        this.isLoading = false;
      }
    },
    async saveAccountingLedger() {
      if (!this.accountingLedgerHumanId.trim()) {
        this.notify('Please enter an Accounting Ledger Id', 'is-warning');
        return;
      }

      try {
        this.isSaving = true;

        if (this.currentMapping) {
          // Update existing mapping
          this.currentMapping = await AccountingLedgerService.updateMapping({
            id: this.currentMapping.id,
            entityId: this.customerId,
            entityType: 'customer',
            accountingLedgerHumanId: this.accountingLedgerHumanId,
          });
        } else {
          // Create new mapping
          this.currentMapping = await AccountingLedgerService.createMapping({
            entityId: this.customerId,
            entityType: 'customer',
            accountingLedgerHumanId: this.accountingLedgerHumanId,
          });
        }

        this.originalLedgerHumanId = this.accountingLedgerHumanId;
        this.notify('Accounting Ledger saved successfully', 'is-success');
      } catch (error) {
        console.error('Error saving accounting ledger:', error);
        const errorMessage = this.getErrorMessage(error);
        this.notify(errorMessage, 'is-danger');
      } finally {
        this.isSaving = false;
      }
    },
    async clearAccountingLedger() {
      const { result } = await this.$buefy.dialog.confirm({
        message: 'Are you sure you want to clear the Accounting Ledger Id?',
        closeOnConfirm: true,
      });

      if (result) {
        try {
          this.isSaving = true;
          if (this.currentMapping) {
            await AccountingLedgerService.deleteMapping(this.currentMapping.id);
            this.currentMapping = null;
            this.accountingLedgerHumanId = '';
            this.originalLedgerHumanId = '';
            this.notify('Accounting Ledger cleared successfully', 'is-success');
          }
        } catch (error) {
          console.error('Error clearing accounting ledger:', error);
          const errorMessage = this.getErrorMessage(error);
          this.notify(errorMessage, 'is-danger');
        } finally {
          this.isSaving = false;
        }
      }
    },
    getErrorMessage(error) {
      // Check for 400 Bad Request with error message from server
      if (error.response?.status === 400 && error.response?.data?.message) {
        return error.response.data.message;
      }
      // Check for other error response data
      if (error.response?.data?.message) {
        return error.response.data.message;
      }
      // Fallback to generic error message
      return 'An error occurred. Please try again.';
    },
    notify(message, type = 'is-success') {
      this.$buefy.notification.open({
        message,
        type,
        hasIcon: true,
        indefinite: type === 'is-danger',
        closable: type === 'is-danger',
      });
    },
  },
};
</script>

<style scoped lang="scss">
.accounting-ledger-integration {
  .line-items {
    margin-bottom: 15px;
    border: #ddd solid 1px;
    border-radius: 5px;

    .line-item-details {
      display: flex;
      flex-direction: row;
      align-items: center;
      width: 100%;
      gap: 1rem;

      .line-item-info {
        flex: 1;

        &.buttons-container {
          flex: 0;
          display: flex;
          flex-direction: row;
          gap: 0.5rem;
          margin-left: 1rem;
        }
      }

      .system-managed-text {
        flex: 1;
        text-align: left;
        padding: 0.5rem 0;
      }
    }
  }
}
</style>
